import React, { useEffect, useState, useCallback } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import {
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Link,
  Paper as MuiPaper,
  Typography,
  Box,
  TextField,
  IconButton,
  Alert,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Button,
  CircularProgress,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { spacing } from "@mui/system";
import agent from "../../utils/axios";
import LoadingButton from "@mui/lab/LoadingButton";
import { Payment1099 } from "../../types/payment1099";
import { PaymentDetail } from "../../types/paymentDetail";
import { hasAnyRole } from "../../utils/auth";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

// Fixed year for this component - 2025 only
const TAX_YEAR = 2025;

// Type for bill detail records
type BillDetail = {
  programName: string;
  invoiceDate: string;
  amount: number;
  clientBillId: string;
  taxable: boolean;
};

// Expandable Row Component
interface RowProps {
  row: any;
  showFullTin: boolean;
  canViewTin: boolean;
  canDownload: boolean;
  isExpanded: boolean;
  isLoadingDetails: boolean;
  details: BillDetail[];
  onToggleExpand: (id: string) => void;
}

function ExpandableRow({
  row,
  showFullTin,
  canViewTin,
  canDownload,
  isExpanded,
  isLoadingDetails,
  details,
  onToggleExpand,
}: RowProps) {
  const address = `${row.address1}${row.address2 ? `, ${row.address2}` : ""}, ${
    row.city
  }, ${row.state} ${row.postalCode}`;

  return (
    <React.Fragment>
      <TableRow
        sx={{
          "& > *": { borderBottom: isExpanded ? "none" : undefined },
          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
        }}
      >
        <TableCell sx={{ width: 50 }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => onToggleExpand(row.id)}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Button
            component="a"
            href={`/payees/detail/${row.id}`}
            sx={{
              color: "black",
              textTransform: "none",
              textDecoration: "underline",
              justifyContent: "flex-start",
              padding: 0,
              minWidth: 0,
              "&:hover": {
                textDecoration: "underline",
                backgroundColor: "transparent",
              },
            }}
            size="small"
          >
            {row.payeeName}
          </Button>
        </TableCell>
        <TableCell>
          {showFullTin && canViewTin
            ? row.taxId
            : `*****${(row.taxId || "").slice(-4)}`}
        </TableCell>
        <TableCell align="right">
          {new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(row.amount)}
        </TableCell>
        {canDownload && (
          <TableCell align="center">
            <IconButton
              onClick={() => handlePdfDownload(row)}
              size="small"
              color="primary"
            >
              <PictureAsPdfIcon />
            </IconButton>
          </TableCell>
        )}
        <TableCell>{address}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={canDownload ? 6 : 5}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, ml: 6 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                component="div"
                sx={{ fontWeight: "bold", color: "#e65100" }}
              >
                Payment Details
              </Typography>
              {isLoadingDetails ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading details...</Typography>
                </Box>
              ) : details.length > 0 ? (
                <Table size="small" sx={{ maxWidth: 700 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Program</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        Amount
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Taxable
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.map((detail, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor: detail.taxable
                            ? "transparent"
                            : "rgba(158, 158, 158, 0.1)",
                          "& td": {
                            textDecoration: detail.taxable
                              ? "none"
                              : "line-through",
                            color: detail.taxable
                              ? "inherit"
                              : "text.secondary",
                          },
                        }}
                      >
                        <TableCell>{detail.programName}</TableCell>
                        <TableCell>
                          {detail.invoiceDate
                            ? new Date(detail.invoiceDate).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "USD",
                          }).format(detail.amount)}
                        </TableCell>
                        <TableCell align="center">
                          {detail.taxable ? (
                            <Typography
                              variant="body2"
                              sx={{ color: "success.main", fontWeight: "bold" }}
                            >
                              Yes
                            </Typography>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ color: "text.disabled" }}
                            >
                              No
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No bills found
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

function Taxes2025() {
  const [loading, setLoading] = useState(true);
  const [loadingButton, setLoadingButton] = useState(false);
  const [order, setOrder] = useState<"desc" | "asc">("asc");
  const [orderBy, setOrderBy] = useState("payeeName");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [showFullTin, setShowFullTin] = useState(false);

  // Expanded rows state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [billDetails, setBillDetails] = useState<Record<string, BillDetail[]>>(
    {}
  );
  const [loadingDetails, setLoadingDetails] = useState<Set<string>>(new Set());
  const [allPaymentDetails, setAllPaymentDetails] = useState<PaymentDetail[]>(
    []
  );
  const [detailsLoaded, setDetailsLoaded] = useState(false);
  const [paymentCountFilter, setPaymentCountFilter] = useState<string>("all");
  const [clientCountFilter, setClientCountFilter] = useState<string>("all");

  // Check if user has Developer or Admin role
  const canDownload = hasAnyRole(["Developer", "Admin"]);

  const handlePaymentCountFilterChange = (event: SelectChangeEvent) => {
    setPaymentCountFilter(event.target.value);
  };

  const handleClientCountFilterChange = (event: SelectChangeEvent) => {
    setClientCountFilter(event.target.value);
  };

  const toggleTin = () => {
    setShowFullTin((prev) => !prev);
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Pre-fetch all payment details on component mount for faster accordion loading
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const details: PaymentDetail[] =
          await agent.Tax2025.getAllPaymentDetailsByYear(TAX_YEAR.toString());
        setAllPaymentDetails(details);
        setDetailsLoaded(true);
      } catch (error) {
        console.error("Error pre-fetching payment details:", error);
      }
    };

    fetchDetails();
  }, []);

  const handleToggleExpand = useCallback(
    (payeeId: string) => {
      setExpandedRows((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(payeeId)) {
          newSet.delete(payeeId);
        } else {
          newSet.add(payeeId);
        }
        return newSet;
      });

      // Get details from pre-fetched data (no filter on taxable - show all bills)
      if (!billDetails[payeeId]) {
        if (detailsLoaded) {
          const payeeBills = allPaymentDetails
            .filter((detail) => detail.payeeId === payeeId)
            .map((detail) => ({
              programName: detail.programName,
              invoiceDate: detail.paymentDate,
              amount: detail.amount,
              clientBillId: detail.payeeId,
              taxable: detail.taxable,
            }));

          setBillDetails((prev) => ({ ...prev, [payeeId]: payeeBills }));
        } else {
          // Details not yet loaded, show loading state
          setLoadingDetails((prev) => new Set(prev).add(payeeId));
        }
      }
    },
    [billDetails, detailsLoaded, allPaymentDetails]
  );

  // When details finish loading, populate any expanded rows that were waiting
  useEffect(() => {
    if (detailsLoaded && loadingDetails.size > 0) {
      const newBillDetails: Record<string, BillDetail[]> = {};
      loadingDetails.forEach((payeeId) => {
        const payeeBills = allPaymentDetails
          .filter((detail) => detail.payeeId === payeeId)
          .map((detail) => ({
            programName: detail.programName,
            invoiceDate: detail.paymentDate,
            amount: detail.amount,
            clientBillId: detail.payeeId,
            taxable: detail.taxable,
          }));
        newBillDetails[payeeId] = payeeBills;
      });

      setBillDetails((prev) => ({ ...prev, ...newBillDetails }));
      setLoadingDetails(new Set());
    }
  }, [detailsLoaded, loadingDetails, allPaymentDetails]);

  const fetchDataAndDownloadCSV = async () => {
    setLoadingButton(true);
    const { data } = await agent.Tax2025.getAllPaymentsByYear(
      TAX_YEAR.toString()
    );
    const sanitizedData = data.map(
      ({ paymentStatus, pdfVisible, ...rest }: any) => rest
    );
    const headers = Object.keys(sanitizedData[0]).join(",");

    const rows = sanitizedData.map((row: any) =>
      Object.values(row)
        .map((value) => `"${value}"`)
        .join(",")
    );
    const csvContent = [headers, ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `1099Payees${TAX_YEAR}_Tax2025.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setLoadingButton(false);
  };

  const fetchDataAndDownloadDetailCSV = async () => {
    setLoadingButton(true);
    try {
      const detailedData: PaymentDetail[] =
        await agent.Tax2025.getAllPaymentDetailsByYear(
          TAX_YEAR.toString(),
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          paymentCountFilter,
          clientCountFilter
        );

      const paymentSuffix =
        paymentCountFilter === "multiple"
          ? "_MultiplePayments"
          : paymentCountFilter === "single"
          ? "_SinglePayment"
          : "";

      const clientSuffix =
        clientCountFilter === "compassAndOther" ? "_CompassAndOther" : "";

      const filterSuffix = paymentSuffix + clientSuffix;

      const headers =
        "Payee ID,Name,Address,Amount,Tax ID,Program Name,Taxable,Payment Date,Requesting Client";
      const rows = detailedData.map((row: PaymentDetail) =>
        [
          row.payeeId,
          row.payeeName,
          `${row.address1}${row.address2 ? `, ${row.address2}` : ""}, ${
            row.city
          }, ${row.state} ${row.postalCode}`,
          row.amount,
          row.taxId,
          row.programName,
          row.taxable ? "Yes" : "No",
          new Date(row.paymentDate).toLocaleDateString(),
          row.requestingClient,
        ]
          .map((value) => `"${value}"`)
          .join(",")
      );
      const csvContent = [headers, ...rows].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `1099PayeesDetail${TAX_YEAR}${filterSuffix}_Tax2025.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading detail CSV:", error);
    } finally {
      setLoadingButton(false);
    }
  };

  // Fetch data with server-side pagination, sorting, and search
  useEffect(() => {
    async function getPayments() {
      setLoading(true);
      try {
        const response = await agent.Tax2025.getAllPaymentsByYear(
          TAX_YEAR.toString(),
          page,
          rowsPerPage,
          orderBy,
          order,
          debouncedSearchTerm
        );
        setData(response.data);
        setTotalCount(response.count);
      } finally {
        setLoading(false);
      }
    }

    getPayments();
  }, [page, rowsPerPage, debouncedSearchTerm, orderBy, order]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Loading skeleton rows
  const renderSkeletonRows = () => {
    return Array.from({ length: 10 }).map((_, idx) => (
      <TableRow key={idx}>
        <TableCell>
          <Skeleton width={24} />
        </TableCell>
        <TableCell>
          <Skeleton width={150} />
        </TableCell>
        <TableCell>
          <Skeleton width={100} />
        </TableCell>
        <TableCell>
          <Skeleton width={80} />
        </TableCell>
        {canDownload && (
          <TableCell>
            <Skeleton width={40} />
          </TableCell>
        )}
        <TableCell>
          <Skeleton width={250} />
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <React.Fragment>
      <Helmet title="Payments 2025" />

      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 2,
              mb: 1,
            }}
          >
            <Typography
              variant="h3"
              display="inline"
              sx={{ color: "#e65100", lineHeight: 1 }}
            >
              Payments 2025
            </Typography>
            <Chip
              label="For 2025 Payments Only"
              color="warning"
              size="small"
              sx={{
                fontWeight: "bold",
                fontSize: "0.75rem",
                height: "24px",
                position: "relative",
                top: "-2px",
              }}
            />
          </Box>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Typography sx={{ color: "#e65100" }}>Payments 2025</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Alert
        severity="warning"
        sx={{
          mb: 3,
          backgroundColor: "#fff3e0",
          borderLeft: "4px solid #ff9800",
          "& .MuiAlert-icon": {
            color: "#e65100",
          },
        }}
      >
        <strong>2025 Tax Year Only</strong> - This page displays 1099 tax data
        from the CSIPayTax2025 database. Use this to provide 1099 forms to
        payees while production updates are pending.
      </Alert>

      <Paper
        p={2}
        sx={{
          borderLeft: "4px solid #ff9800",
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            label="Search 2025 Payments"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search by Name, Address, Tax ID..."
            size="small"
            sx={{
              width: "350px",
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "#ff9800",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#e65100",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#e65100",
              },
            }}
          />
          {canDownload && (
            <>
              <LoadingButton
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: "#ff9800",
                  "&:hover": {
                    backgroundColor: "#f57c00",
                  },
                  whiteSpace: "nowrap",
                }}
                onClick={fetchDataAndDownloadCSV}
                loading={loadingButton}
              >
                Download 2025 1099
              </LoadingButton>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="payment-count-filter-label">
                  Payment Filter
                </InputLabel>
                <Select
                  labelId="payment-count-filter-label"
                  id="payment-count-filter"
                  value={paymentCountFilter}
                  label="Payment Filter"
                  onChange={handlePaymentCountFilterChange}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ff9800",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e65100",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e65100",
                    },
                  }}
                >
                  <MenuItem value="all">All Payments</MenuItem>
                  <MenuItem value="multiple">Multiple Payments</MenuItem>
                  <MenuItem value="single">Single Payment</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="client-count-filter-label">
                  Client Filter
                </InputLabel>
                <Select
                  labelId="client-count-filter-label"
                  id="client-count-filter"
                  value={clientCountFilter}
                  label="Client Filter"
                  onChange={handleClientCountFilterChange}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ff9800",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e65100",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e65100",
                    },
                  }}
                >
                  <MenuItem value="all">All Clients</MenuItem>
                  <MenuItem value="compassAndOther">Compass + Other</MenuItem>
                </Select>
              </FormControl>
              <LoadingButton
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: "#e65100",
                  "&:hover": {
                    backgroundColor: "#bf360c",
                  },
                  whiteSpace: "nowrap",
                }}
                onClick={fetchDataAndDownloadDetailCSV}
                loading={loadingButton}
              >
                Download 2025 Details
              </LoadingButton>
            </>
          )}
        </Box>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper
            sx={{
              borderTop: "4px solid #ff9800",
            }}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#fff3e0" }}>
                    <TableCell sx={{ width: 50 }} />
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === "payeeName"}
                        direction={orderBy === "payeeName" ? order : "asc"}
                        onClick={() => handleRequestSort("payeeName")}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <TableSortLabel
                          active={orderBy === "taxId"}
                          direction={orderBy === "taxId" ? order : "asc"}
                          onClick={() => handleRequestSort("taxId")}
                        >
                          Tax ID
                        </TableSortLabel>
                        {canDownload && (
                          <IconButton
                            size="small"
                            onClick={toggleTin}
                            sx={{ padding: 0.5 }}
                          >
                            {showFullTin ? (
                              <VisibilityOffIcon fontSize="small" />
                            ) : (
                              <VisibilityIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={orderBy === "amount"}
                        direction={orderBy === "amount" ? order : "asc"}
                        onClick={() => handleRequestSort("amount")}
                      >
                        Amount
                      </TableSortLabel>
                    </TableCell>
                    {canDownload && <TableCell align="center">1099</TableCell>}
                    <TableCell>Address</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading
                    ? renderSkeletonRows()
                    : data.map((row) => (
                        <ExpandableRow
                          key={row.id}
                          row={row}
                          showFullTin={showFullTin}
                          canViewTin={canDownload}
                          canDownload={canDownload}
                          isExpanded={expandedRows.has(row.id)}
                          isLoadingDetails={loadingDetails.has(row.id)}
                          details={billDetails[row.id] || []}
                          onToggleExpand={handleToggleExpand}
                        />
                      ))}
                  {!loading && data.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={canDownload ? 6 : 5}
                        align="center"
                        sx={{ py: 4 }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ color: "#e65100", fontWeight: "bold" }}
                        >
                          No payments found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100, 200]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default Taxes2025;

function handlePdfDownload(row: Payment1099): void {
  agent.Tax2025.download1099PDF(row)
    .then((response: Blob) => {
      const url = URL.createObjectURL(response);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${row.payeeName}_${row.year}_1099.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    })
    .catch((error) => {
      console.error("Error downloading PDF:", error);
    });
}
